import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useHistory, useLocation } from 'react-router'
import { Badge, Button, Card, CardBody, Col, Container, Input, Row, Table } from 'reactstrap'

import SalesUsersApi from 'api/admins/sales-users-api'
import { TitlePage } from 'components/atoms'
import { ALERT_TYPES } from 'constants/alert-constants'

const BASE_PATH = '/app/sales/users'
const PER_PAGE_OPTIONS = [25, 50, 100]

/**
 * Safe extractor for main detail
 * Supports both:
 * - flattened index response (name/phone/state at root)
 * - nested details array response
 */

const KYC_STATE_OPTIONS = [
  { value: '', label: 'All KYC Status' },
  { value: 'waiting_for_verification', label: 'Waiting For Verification' },
  { value: 'created', label: 'Created' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'verified', label: 'Verified' },
]

const KYC_STATE_META = {
  waiting_for_verification: { label: 'Waiting For Verification', color: 'warning' },
  created: { label: 'Created', color: 'secondary' },
  suspended: { label: 'Suspended', color: 'danger' },
  rejected: { label: 'Rejected', color: 'danger' },
  verified: { label: 'Verified', color: 'success' },
}

const getMainDetail = (u) => {
  if (u?.detail) return u.detail
  const details = Array.isArray(u?.details) ? u.details : []
  return details.find((d) => d?.primary) || details[0] || {}
}

const renderKycBadge = (state) => {
  const meta = KYC_STATE_META[state] || { label: state || '-', color: 'secondary' }
  return <Badge color={meta.color}>{meta.label}</Badge>
}

const normalizeSearchText = (value) => String(value || '').trim().toLowerCase()
const normalizeSearchDigits = (value) => String(value || '').replace(/\D/g, '')

const buildPhoneSearchValues = (detail) => {
  const rawPhone = normalizeSearchDigits(detail?.phone || detail?.formatted_phone_number)
  const localPhone = rawPhone.replace(/^62/, '').replace(/^0/, '')
  const values = [rawPhone, localPhone]

  if (localPhone) {
    values.push(`0${localPhone}`)
    values.push(`62${localPhone}`)
  }

  return [...new Set(values.filter(Boolean))]
}

const detailMatchesSearch = (detail, keyword) => {
  const q = normalizeSearchText(keyword)
  if (!q) return false

  const qDigits = normalizeSearchDigits(q)
  const searchableText = [detail?.name, detail?.phone, detail?.formatted_phone_number, detail?.label]
    .map(normalizeSearchText)
    .filter(Boolean)
    .join(' ')

  if (searchableText.includes(q)) return true
  if (!qDigits) return false

  return buildPhoneSearchValues(detail).some((phone) => phone.includes(qDigits))
}

const getUserDetails = (u) => {
  const details = Array.isArray(u?.details) ? u.details.filter(Boolean) : []
  if (details.length > 0) return details

  const main = getMainDetail(u)
  return Object.keys(main || {}).length > 0 ? [main] : []
}

const sortDetailsForSearch = (details, keyword) => {
  const q = normalizeSearchText(keyword)

  return details.slice().sort((a, b) => {
    const aMatch = q && detailMatchesSearch(a, q) ? 1 : 0
    const bMatch = q && detailMatchesSearch(b, q) ? 1 : 0
    if (aMatch !== bMatch) return bMatch - aMatch

    const aPrimary = a?.primary ? 1 : 0
    const bPrimary = b?.primary ? 1 : 0
    if (aPrimary !== bPrimary) return bPrimary - aPrimary

    const ta = a?.updated_at || a?.created_at ? new Date(a.updated_at || a.created_at).getTime() : 0
    const tb = b?.updated_at || b?.created_at ? new Date(b.updated_at || b.created_at).getTime() : 0
    if (ta !== tb) return tb - ta

    return Number(b?.id || 0) - Number(a?.id || 0)
  })
}

const detailDisplayPhone = (detail) => detail?.formatted_phone_number || detail?.phone || '-'

const SalesUsersListPage = ({ pageUtils }) => {
  const history = useHistory()
  const location = useLocation()

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search])

  const initialQ = queryParams.get('q') || ''
  const appliedQ = queryParams.get('q') || ''
  const initialPage = parseInt(queryParams.get('page') || '1', 10)
  const initialPerPage = parseInt(queryParams.get('per_page') || '25', 10)
  const initialStartAt = queryParams.get('start_at') || ''
  const initialEndAt = queryParams.get('end_at') || ''
  const initialState = queryParams.get('state') || ''

  const [q, setQ] = useState(initialQ)
  const [page, setPage] = useState(initialPage)
  const [perPage, setPerPage] = useState(initialPerPage)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)

  const [exportStartAt, setExportStartAt] = useState(initialStartAt)
  const [exportEndAt, setExportEndAt] = useState(initialEndAt)
  const [stateFilter, setStateFilter] = useState(initialState)
  const [expandedUserIds, setExpandedUserIds] = useState({})

  const [users, setUsers] = useState([])
  const [meta, setMeta] = useState({
    current_page: 1,
    per_page: 25,
    total_pages: 1,
    total_count: 0,
    can_export_csv: false,
  })

  const fetchUsers = useCallback(
    async ({ q, page, perPage, startAt, endAt, state }) => {
      setLoading(true)
      try {
        const response = await SalesUsersApi.index({
          params: {
            q: q || undefined,
            page,
            per_page: perPage,
            start_at: startAt || undefined,
            end_at: endAt || undefined,
            state: state || undefined,
          },
        })

        const data = response?.data || {}
        setUsers(Array.isArray(data?.users) ? data.users : [])
        setExpandedUserIds({})
        setMeta(
          data?.meta || {
            current_page: page,
            per_page: perPage,
            total_pages: 1,
            total_count: 0,
            can_export_csv: false,
          }
        )
      } catch (e) {
        pageUtils?.setApiErrorMsg?.(e?.response?.data || { message: e?.message || 'Request gagal.' })
        pageUtils?.setAlertMsg?.('Gagal memuat user list.', ALERT_TYPES.ERROR)
        setUsers([])
        setMeta({
          current_page: 1,
          per_page: perPage,
          total_pages: 1,
          total_count: 0,
          can_export_csv: false,
        })
      } finally {
        setLoading(false)
      }
    },
    [pageUtils]
  )

  useEffect(() => {
    const qp = new URLSearchParams(location.search)

    const nextQ = qp.get('q') || ''
    const nextPage = parseInt(qp.get('page') || '1', 10)
    const nextPerPage = parseInt(qp.get('per_page') || '25', 10)
    const nextStartAt = qp.get('start_at') || ''
    const nextEndAt = qp.get('end_at') || ''
    const nextState = qp.get('state') || ''

    setQ(nextQ)
    setPage(nextPage)
    setPerPage(nextPerPage)
    setExportStartAt(nextStartAt)
    setExportEndAt(nextEndAt)
    setStateFilter(nextState)

    fetchUsers({
      q: nextQ,
      page: nextPage,
      perPage: nextPerPage,
      startAt: nextStartAt,
      endAt: nextEndAt,
      state: nextState,
    })
  }, [location.search, fetchUsers])

  const updateUrl = (nextQ, nextPage, nextPerPage, nextStartAt, nextEndAt, nextState) => {
    const qp = new URLSearchParams()

    if (nextQ) qp.set('q', nextQ)
    qp.set('page', String(nextPage))
    qp.set('per_page', String(nextPerPage))

    if (nextStartAt) qp.set('start_at', nextStartAt)
    if (nextEndAt) qp.set('end_at', nextEndAt)
    if (nextState) qp.set('state', nextState)

    history.push(`${BASE_PATH}?${qp.toString()}`)
  }

  const onSearch = () => {
    updateUrl(
      q.trim(),
      1,
      perPage,
      exportStartAt.trim(),
      exportEndAt.trim(),
      stateFilter.trim()
    )
  }

  const onChangePage = (nextPage) => {
    updateUrl(
      q,
      nextPage,
      perPage,
      exportStartAt.trim(),
      exportEndAt.trim(),
      stateFilter.trim()
    )
  }

  const onChangePerPage = (value) => {
    updateUrl(
      q,
      1,
      value,
      exportStartAt.trim(),
      exportEndAt.trim(),
      stateFilter.trim()
    )
  }

  const onExportCsv = async () => {
    const startVal = exportStartAt?.trim()
    const endVal = exportEndAt?.trim()
    const stateVal = stateFilter?.trim()

    if (startVal && endVal) {
      const startDate = new Date(startVal)
      const endDate = new Date(endVal)

      if (!Number.isNaN(startDate?.getTime?.()) && !Number.isNaN(endDate?.getTime?.()) && startDate > endDate) {
        pageUtils?.setAlertMsg?.('Range tanggal export tidak valid (Start > End).', ALERT_TYPES.ERROR)
        return
      }
    }

    setExporting(true)
    try {
      const response = await SalesUsersApi.exportCsv({
        q: q.trim(),
        start_at: startVal || undefined,
        end_at: endVal || undefined,
        state: stateVal || undefined,
      })

      const blob = new Blob([response?.data], { type: 'text/csv;charset=utf-8;' })

      const disposition = response?.headers?.['content-disposition']
      let filename = 'users.csv'
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/i)
        if (match && match[1]) filename = match[1]
      }

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      let apiErr = e?.response?.data

      try {
        if (apiErr instanceof Blob) {
          const text = await apiErr.text()
          apiErr = JSON.parse(text)
        }
      } catch (_) {}

      pageUtils?.setApiErrorMsg?.(apiErr || { message: e?.message || 'Export gagal.' })
      pageUtils?.setAlertMsg?.(apiErr?.message || 'Gagal export CSV.', ALERT_TYPES.ERROR)
    } finally {
      setExporting(false)
    }
  }

  const goCreate = () => history.push(`${BASE_PATH}/create`)
  const goDetail = (userId) => history.push(`${BASE_PATH}/${userId}`)
  const toggleUserDetails = (userId) => {
    setExpandedUserIds((prev) => ({ ...prev, [userId]: !prev[userId] }))
  }

  return (
    <>
      <TitlePage mainTitle="Users" />

      <Container fluid>
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                {/* SEARCH + PER PAGE + ACTIONS */}
                <Row className="align-items-center">
                  <Col md="4">
                    <Input
                      value={q}
                      placeholder="Search email / phone / name..."
                      onChange={(e) => setQ(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') onSearch()
                      }}
                    />
                  </Col>

                  <Col md="3">
                    <Input
                      type="select"
                      value={stateFilter}
                      onChange={(e) => setStateFilter(e.target.value)}
                    >
                      {KYC_STATE_OPTIONS.map((opt) => (
                        <option key={opt.value || 'all'} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Input>
                  </Col>

                  <Col md="2">
                    <Input type="select" value={perPage} onChange={(e) => onChangePerPage(Number(e.target.value))}>
                      {PER_PAGE_OPTIONS.map((n) => (
                        <option key={n} value={n}>
                          {n} / page
                        </option>
                      ))}
                    </Input>
                  </Col>

                  <Col md="3">
                    <div className="d-flex justify-content-end align-items-center text-nowrap">
                      {meta.can_export_csv ? (
                        <Button
                          color="secondary"
                          onClick={onExportCsv}
                          disabled={loading || exporting}
                          className="mr-3"
                        >
                          {exporting ? 'Preparing…' : 'Export CSV'}
                        </Button>
                      ) : null}

                      <div className="d-flex align-items-center">
                        <Button color="primary" onClick={onSearch} disabled={loading}>
                          Search
                        </Button>

                        <Button color="success" onClick={goCreate} className="ml-2">
                          + Create
                        </Button>
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* DATETIME RANGE FILTER (JKT) */}
                <Row className="mt-2">
                  <Col md="5">
                    <small className="text-muted d-block mb-1">Start Datetime (JKT)</small>
                    <Input
                      type="datetime-local"
                      value={exportStartAt}
                      onChange={(e) => setExportStartAt(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') onSearch()
                      }}
                    />
                  </Col>

                  <Col md="3">
                    <small className="text-muted d-block mb-1">End Datetime (JKT)</small>
                    <Input
                      type="datetime-local"
                      value={exportEndAt}
                      onChange={(e) => setExportEndAt(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') onSearch()
                      }}
                    />
                  </Col>

                  <Col md="4" />
                </Row>

                {/* TABLE */}
                <div className="mt-3">
                  {loading ? (
                    <div>Loading...</div>
                  ) : (
                    <>
                      <Table bordered responsive hover>
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Email</th>
                            <th>User Details</th>
                            <th>KYC Status</th>
                            <th>Created</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {users.map((u) => {
                            const details = sortDetailsForSearch(getUserDetails(u), appliedQ)
                            const topDetail = details[0] || getMainDetail(u)
                            const state = topDetail?.state || u?.state || ''
                            const isExpanded = !!expandedUserIds[u.id]
                            const matchedCount = appliedQ ? details.filter((d) => detailMatchesSearch(d, appliedQ)).length : 0

                            return (
                              <React.Fragment key={u.id}>
                                <tr>
                                  <td>{u.id}</td>
                                  <td>{u.email || '-'}</td>
                                  <td>
                                    <div className="font-weight-bold">
                                      {topDetail?.name || u?.name || '-'}
                                      {topDetail?.primary ? (
                                        <Badge color="primary" className="ml-2">
                                          Primary
                                        </Badge>
                                      ) : null}
                                      {appliedQ && detailMatchesSearch(topDetail, appliedQ) ? (
                                        <Badge color="warning" className="ml-2">
                                          Matched
                                        </Badge>
                                      ) : null}
                                    </div>

                                    <div className="text-muted small">{detailDisplayPhone(topDetail)}</div>

                                    <Button
                                      type="button"
                                      size="sm"
                                      color="link"
                                      className="p-0 mt-1"
                                      onClick={() => toggleUserDetails(u.id)}
                                    >
                                      {isExpanded ? 'Hide' : 'Show'} {details.length} detail{details.length === 1 ? '' : 's'}
                                      {matchedCount > 0 ? ` (${matchedCount} matched)` : ''}
                                    </Button>
                                  </td>

                                  <td>{renderKycBadge(state)}</td>
                                  <td>{u.created_at ? new Date(u.created_at).toLocaleString() : '-'}</td>
                                  <td className="text-right">
                                    <Button size="sm" color="info" onClick={() => goDetail(u.id)}>
                                      Open
                                    </Button>
                                  </td>
                                </tr>

                                {isExpanded ? (
                                  <tr>
                                    <td colSpan={6} className="bg-light">
                                      <Table size="sm" bordered responsive className="mb-0 bg-white">
                                        <thead>
                                          <tr>
                                            <th>Detail ID</th>
                                            <th>Name</th>
                                            <th>Phone</th>
                                            <th>KYC Status</th>
                                            <th>Lead Source</th>
                                            <th>Updated</th>
                                          </tr>
                                        </thead>

                                        <tbody>
                                          {details.map((detail) => {
                                            const matched = appliedQ && detailMatchesSearch(detail, appliedQ)

                                            return (
                                              <tr
                                                key={detail.id || `${u.id}-${detail.name}-${detail.phone}`}
                                                className={matched ? 'table-warning' : ''}
                                              >
                                                <td>{detail.id || '-'}</td>
                                                <td>
                                                  {detail.name || '-'}
                                                  {detail.primary ? (
                                                    <Badge color="primary" className="ml-2">
                                                      Primary
                                                    </Badge>
                                                  ) : null}
                                                  {matched ? (
                                                    <Badge color="warning" className="ml-2">
                                                      Matched
                                                    </Badge>
                                                  ) : null}
                                                </td>
                                                <td>{detailDisplayPhone(detail)}</td>
                                                <td>{renderKycBadge(detail.state)}</td>
                                                <td>{detail.lead_source || '-'}</td>
                                                <td>{detail.updated_at ? new Date(detail.updated_at).toLocaleString() : '-'}</td>
                                              </tr>
                                            )
                                          })}
                                        </tbody>
                                      </Table>
                                    </td>
                                  </tr>
                                ) : null}
                              </React.Fragment>
                            )
                          })}

                          {users.length === 0 && (
                            <tr>
                              <td colSpan={6}>No results</td>
                            </tr>
                          )}
                        </tbody>
                      </Table>

                      {/* PAGINATION */}
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div>
                          Showing page {meta.current_page || 1} of {meta.total_pages || 1} — Total {meta.total_count || 0} users
                        </div>

                        <div>
                          <Button
                            size="sm"
                            disabled={(meta.current_page || 1) <= 1}
                            onClick={() => onChangePage((meta.current_page || 1) - 1)}
                          >
                            Previous
                          </Button>{' '}
                          <Button
                            size="sm"
                            disabled={(meta.current_page || 1) >= (meta.total_pages || 1)}
                            onClick={() => onChangePage((meta.current_page || 1) + 1)}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  )
}

SalesUsersListPage.propTypes = {
  pageUtils: PropTypes.object,
}

export default SalesUsersListPage
